import React from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AIModelConfiguration = ({ config, onUpdate }) => {
  // Updated model list with GPT-5 as default
  const availableModels = [
    { 
      value: 'gpt-5', 
      label: 'GPT-5', 
      description: 'Latest flagship model with advanced reasoning and coding capabilities (272K context)' 
    },
    { 
      value: 'gpt-5-mini', 
      label: 'GPT-5 Mini', 
      description: 'Cost-effective variant with similar capabilities, optimized for efficiency' 
    },
    { 
      value: 'gpt-5-nano', 
      label: 'GPT-5 Nano', 
      description: 'Ultra-fast variant for basic tasks like classification' 
    },
    { 
      value: 'gpt-4.1', 
      label: 'GPT-4.1', 
      description: 'Multimodal model strong in coding and instruction-following (128K context)' 
    },
    { 
      value: 'gpt-4.1-mini', 
      label: 'GPT-4.1 Mini', 
      description: 'Affordable, fast variant with near GPT-4.1 performance' 
    },
    { 
      value: 'gpt-4o', 
      label: 'GPT-4o', 
      description: 'Versatile multimodal model excelling in non-English and vision tasks' 
    },
    { 
      value: 'gpt-4o-mini', 
      label: 'GPT-4o Mini', 
      description: 'Cost-optimized model for general use cases' 
    },
    { 
      value: 'gpt-4', 
      label: 'GPT-4', 
      description: 'Previous generation flagship model' 
    }
  ];

  const providerOptions = [
    { value: 'openai', label: 'OpenAI', description: 'OpenAI GPT models' },
    { value: 'claude', label: 'Claude', description: 'Anthropic Claude models' },
    { value: 'gemini', label: 'Gemini', description: 'Google Gemini models' }
  ];

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  const resetToDefaults = () => {
    const defaults = {
      provider: 'openai',
      model: 'gpt-5',
      temperature: 0.67,
      api_key: ''
    };
    onUpdate(defaults);
  };

  const isGPT5Model = config?.model?.startsWith('gpt-5');

  return (
    <div className="space-y-6">
      {/* Model Selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Brain" size={20} className="mr-2 text-primary" />
          Model Selection
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Primary Model"
            options={availableModels}
            value={config?.model || 'gpt-5'}
            onChange={(value) => handleInputChange('model', value)}
            required
            description="Main model for AI responses"
          />
          
          <Select
            label="Provider"
            options={providerOptions}
            value={config?.provider || 'openai'}
            onChange={(value) => handleInputChange('provider', value)}
            required
            description="AI service provider"
          />
        </div>
      </div>

      {/* General Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="Sliders" size={20} className="mr-2 text-primary" />
            Model Configuration
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
          >
            Reset Defaults
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Temperature"
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={config?.temperature || 0.67}
            onChange={(e) => handleInputChange('temperature', parseFloat(e?.target?.value) || 0.67)}
            description="Controls randomness in responses (0-2)"
          />

          <Input
            label="API Key"
            type="password"
            value={config?.api_key || ''}
            onChange={(e) => handleInputChange('api_key', e?.target?.value)}
            placeholder="Optional: Custom API key"
            description="Leave empty to use default key"
          />
        </div>

        <div className="mt-6">
          <label className="text-sm font-medium text-foreground mb-2 block">
            System Prompt
          </label>
          <textarea
            value={config?.system_prompt || ''}
            onChange={(e) => handleInputChange('system_prompt', e?.target?.value)}
            placeholder="You are a helpful AI assistant..."
            className="w-full px-3 py-2 bg-background border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-sm"
            rows={4}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Defines the AI's personality and behavior
          </p>
        </div>
      </div>

      {/* GPT-5 Specific Information */}
      {isGPT5Model && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Icon name="Info" size={20} className="mr-2 text-blue-600" />
            <h3 className="text-lg font-semibold text-blue-900">GPT-5 Features</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-blue-600">272K</div>
              <div className="text-xs text-gray-600">Context Tokens</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-green-600">Advanced</div>
              <div className="text-xs text-gray-600">Reasoning</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-purple-600">Multimodal</div>
              <div className="text-xs text-gray-600">Capabilities</div>
            </div>
          </div>
          
          <p className="text-sm text-blue-800 mt-4">
            GPT-5 offers enhanced reasoning capabilities and extended context windows for complex conversations.
          </p>
        </div>
      )}
    </div>
  );
};

export default AIModelConfiguration;